import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiGet, apiPut, apiDelete } from '../api/readerApi';
import { clearContentCache } from '../content/contentApi';
import { usePlans } from '../site/plans';
import { limpiarWhatsapp } from './sesion';
import { getSite } from './sites';

/**
 * The reader comes from GET /me (Cognito token from src/auth); without a token
 * they are a visitor. The plan shown here only drives what is painted: the
 * server decides on every request what the reader can actually open.
 */

export const MONEDA = 'USD';
export const PERIODO = 'año';

const PLAN_NUMBER_BY_ID = { visitor: 0, free: 1, tier1: 2, tier2: 3, tier3: 4 };

export const planNumberOf = (planId) => PLAN_NUMBER_BY_ID[planId] ?? 0;
export const planLabelKey = (planId) => `planes.p${planNumberOf(planId)}`;

/** Home card lines per donation level (Def 102: donations, simplified texts); the texts live in i18n. */
const PLAN_CARD_LINES = {
  free: ['home.plan1_1'],
  tier1: ['home.plan2_1'],
  tier2: ['home.plan3_1', 'home.plan3_3'],
  tier3: ['home.plan4_1', 'home.plan4_3', 'home.plan4_4']
};

export const planCardLines = (plan) => PLAN_CARD_LINES[plan.id] || [];
export const isCollaboratorPlan = (plan) => plan.id === 'tier3';

const NO_PERMISSIONS = {
  leerCanalizaciones: false,
  personaliza: false,
  imprimir: false,
  musica: false,
  audioGenerico: false,
  audioPersonal: false,
  ensenanzas: false,
  corpusTerceros: false
};

const planView = (planId, apiPlan) => {
  const features = (apiPlan && apiPlan.features) || {};
  const number = planNumberOf(planId);
  return {
    id: planId,
    numero: number,
    clave: `planes.p${number}`,
    precio: apiPlan ? apiPlan.priceUsd : 0,
    ...NO_PERMISSIONS,
    leerCanalizaciones: Boolean(features.readChannelings),
    personaliza: Boolean(features.nameInText),
    imprimir: Boolean(features.print),
    musica: Boolean(features.music),
    audioGenerico: Boolean(features.audioGeneric),
    audioPersonal: Boolean(features.audioPersonal),
    ensenanzas: Boolean(features.teachings33),
    corpusTerceros: Boolean(features.thirdParty)
  };
};

export const usePlanCards = () => {
  const plans = usePlans();
  return useMemo(() => plans.map((apiPlan) => planView(apiPlan.planId, apiPlan)), [plans]);
};

export const NADIE = { nombre: '', genero: 'm', idioma: 'es', plan: 0, correo: '', whatsapp: '', pais: '' };

const genderOf = (profileGender) => (profileGender === 'f' || profileGender === 'm' ? profileGender : 'n');
const apiGenderOf = (gender) => (gender === 'f' || gender === 'm' ? gender : '');
const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';

const readerFromProfile = (profile) => ({
  id: profile.readerId,
  correo: profile.email || '',
  nombre: profile.name || '',
  idioma: profile.locale || 'es',
  pais: profile.country || '',
  whatsapp: profile.whatsapp || '',
  genero: genderOf(profile.gender),
  plan: planNumberOf(profile.plan),
  desde: (profile.createdAt || '').slice(0, 10)
});

const LectorContexto = createContext({
  lector: NADIE,
  plan: planView('visitor'),
  numeroPlan: 0,
  planName: 'visitor',
  token: null,
  ready: false,
  identificado: false,
  fijar: async () => {},
  entrar: () => {},
  salir: async () => {},
  borrarCuenta: async () => ({ ok: false }),
  exportarDatos: async () => null
});

export const ProveedorLector = ({ children }) => {
  const { accessToken, loading: authLoading, login, logout, dropSession } = useAuth();
  const plans = usePlans();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!accessToken) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }
    let alive = true;
    setProfileLoading(true);
    apiGet(`/me?site=${encodeURIComponent(getSite())}`, { token: accessToken }).then((result) => {
      if (!alive) return;
      if (result.status === 401) {
        dropSession();
        return;
      }
      setProfile(result.ok ? result.data : null);
      setProfileLoading(false);
    });
    return () => { alive = false; };
  }, [accessToken, authLoading, dropSession]);

  const ready = !authLoading && !profileLoading;
  const token = profile ? accessToken : null;

  const fijar = useCallback(async (changes) => {
    if (!profile) return { ok: false };
    const body = {
      name: changes.nombre ?? profile.name ?? '',
      locale: changes.idioma ?? profile.locale ?? '',
      country: changes.pais ?? profile.country ?? '',
      whatsapp: changes.whatsapp !== undefined ? limpiarWhatsapp(changes.whatsapp) : (profile.whatsapp || ''),
      gender: changes.genero !== undefined ? apiGenderOf(changes.genero) : (profile.gender || ''),
      timeZone: browserTimeZone()
    };
    const result = await apiPut('/me', body, { token });
    if (result.ok) setProfile((previous) => ({ ...previous, ...(result.data || body) }));
    return result;
  }, [profile, token]);

  const exportarDatos = useCallback(async () => {
    const result = await apiGet('/me/export', { token });
    return result.ok ? result.data : null;
  }, [token]);

  const borrarCuenta = useCallback(async () => {
    const result = await apiDelete('/me', { token });
    if (result.ok) {
      clearContentCache();
      await logout();
    }
    return result;
  }, [token, logout]);

  const valor = useMemo(() => {
    const lector = profile ? readerFromProfile(profile) : NADIE;
    const planId = profile ? profile.plan || 'visitor' : 'visitor';
    return {
      lector,
      plan: planView(planId, plans.find((apiPlan) => apiPlan.planId === planId)),
      numeroPlan: lector.plan,
      planName: planId,
      token,
      ready,
      identificado: Boolean(profile),
      fijar,
      entrar: login,
      salir: logout,
      borrarCuenta,
      exportarDatos
    };
  }, [profile, plans, token, ready, fijar, login, logout, borrarCuenta, exportarDatos]);

  return <LectorContexto.Provider value={valor}>{children}</LectorContexto.Provider>;
};

export const useLector = () => useContext(LectorContexto);
